import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { StatCard, CurrencyDisplay } from '@/components/ui/stat-card';
import { ListPagination } from '@/components/ListPagination';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import TransactionSelector from '@/components/ui/TransactionSelector';
import { MonthRangePicker } from '@/components/MonthRangePicker';
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { ClipboardCheck, MoreVertical, Search } from 'lucide-react';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { toast } from 'sonner';
import { useDebounce } from '@/hooks/useDebounce';
import {
    businessDayEndInstant, businessDayStartInstant, businessToday, businessWeekBounds, formatBusinessDate,
} from '@/lib/format';

// ─── Types ───────────────────────────────────────────────────────────────

interface WeeklyPayoutRow {
    week_start: string;
    due_date: string;
    vehicle_count: number;
    total_repayments: number;
    total_partner_amount: number;
    payout_amount: number;
    payout_id: number | null;
    payout_transaction_id: number | null;
    payout_created_at: string | null;
    payout_description: string | null;
    status: string;
    repayment_ids: number[];
    partner_id: number;
    partner_name: string;
    total_count: number;
}

// ─── Helpers ─────────────────────────────────────────────────────────────

const formatCurrency = (amount: number) =>
    new Intl.NumberFormat('en-GH', { style: 'currency', currency: 'GHS' }).format(amount);

const getStatusBadge = (status: string) => {
    switch (status) {
        case 'paid':
            return <Badge className="bg-green-100 text-green-700 border-green-200">Paid</Badge>;
        case 'partial':
            return <Badge className="bg-orange-100 text-orange-700 border-orange-200">Partial</Badge>;
        case 'due':
            return <Badge className="bg-red-100 text-red-700 border-red-200">Due</Badge>;
        case 'upcoming':
            return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Upcoming</Badge>;
        default:
            return <Badge variant="outline" className="text-muted-foreground">—</Badge>;
    }
};

// ─── Component ───────────────────────────────────────────────────────────

export default function PartnerPayoutAssignments() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const PAGE_SIZE = 15;
    const [currentPage, setCurrentPage] = useState(1);

    // ─── Partner search ───────────────────────────────────────────────
    const [partnerSearch, setPartnerSearch] = useState('');
    const debouncedSearch = useDebounce(partnerSearch, 300);

    // ─── Date range (defaults to last 12 months) ──────────────────────
    // Business-calendar months; the RPC bounds are built from their parts, never toISOString().
    const [monthRange, setMonthRange] = useState({
        from: startOfMonth(subMonths(businessToday(), 11)),
        to: endOfMonth(businessToday()),
    });
    const fromInstant = businessDayStartInstant(monthRange.from);
    const toInstant = businessDayEndInstant(monthRange.to);

    const handleMonthRangeChange = (newRange: { from: Date; to: Date }) => {
        setMonthRange(newRange);
        setCurrentPage(1);
    };

    // ─── Status filter ────────────────────────────────────────────────
    const [statusFilter, setStatusFilter] = useState<string>('all');

    // ─── Assignment sheet state ───────────────────────────────────────
    const [assignSheetOpen, setAssignSheetOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<WeeklyPayoutRow | null>(null);
    const [selectedTransactionId, setSelectedTransactionId] = useState<number | null>(null);
    const [editingTransactionId, setEditingTransactionId] = useState<number | null>(null);

    // ─── Data Fetching ────────────────────────────────────────────────

    const { data: rawRows, isLoading } = useQuery({
        queryKey: [
            'partner-weekly-payouts',
            fromInstant,
            toInstant,
            statusFilter,
            currentPage,
        ],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_all_partner_weekly_payouts', {
                p_partner_id: null,
                p_from_date: fromInstant,
                p_to_date: toInstant,
                p_status: statusFilter === 'all' ? null : statusFilter,
                p_page: currentPage,
                p_page_size: PAGE_SIZE,
            });
            if (error) throw error;
            return data as WeeklyPayoutRow[];
        },
    });

    // Client-side partner name filter
    const rows = useMemo(() => {
        if (!rawRows) return rawRows;
        if (!debouncedSearch) return rawRows;
        return rawRows.filter(r =>
            r.partner_name.toLowerCase().includes(debouncedSearch.toLowerCase())
        );
    }, [rawRows, debouncedSearch]);

    const totalCount = debouncedSearch ? (rows?.length ?? 0) : (rawRows?.[0]?.total_count ?? 0);
    const totalPages = Math.ceil(totalCount / PAGE_SIZE);

    // ─── Stats (server-side, across all data) ────────────────────────

    const { data: stats } = useQuery({
        queryKey: [
            'admin-payout-assignment-stats',
            fromInstant,
            toInstant,
        ],
        queryFn: async () => {
            const { data, error } = await (supabase as any).rpc('get_admin_payout_assignment_stats', {
                p_from_date: fromInstant,
                p_to_date: toInstant,
            });
            if (error) throw error;
            const row = data?.[0];
            return {
                totalPartnerAmount: row?.total_partner_amount ?? 0,
                paidCount: row?.paid_count ?? 0,
                dueCount: row?.due_count ?? 0,
                totalPaid: row?.total_paid_out ?? 0,
                partnerCount: row?.partner_count ?? 0,
            };
        },
    });

    const safeStats = stats ?? { totalPartnerAmount: 0, paidCount: 0, dueCount: 0, totalPaid: 0, partnerCount: 0 };

    // ─── Payout Creation Mutation ─────────────────────────────────────

    const createPayoutMutation = useMutation({
        mutationFn: async ({ transactionId, repaymentIds }: { transactionId: number; repaymentIds: number[] }) => {
            const rows = repaymentIds.map(repaymentId => ({
                repayment_id: repaymentId,
                transaction_id: transactionId,
            }));

            const { error } = await supabase
                .from('partner_payouts')
                .insert(rows);

            if (error) throw error;
        },
        onSuccess: () => {
            toast.success(editingTransactionId ? 'Payout updated' : 'Payout assigned');
            invalidateAll();
            closeSheet();
        },
        onError: (error: Error) => {
            toast.error(`Failed to ${editingTransactionId ? 'update' : 'assign'} payout: ${error.message}`);
        },
    });

    // ─── Delete Payout Mutation ───────────────────────────────────────

    const deletePayoutMutation = useMutation({
        mutationFn: async (transactionId: number) => {
            const { error } = await supabase
                .from('partner_payouts')
                .delete()
                .eq('transaction_id', transactionId)
                .not('repayment_id', 'is', null);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Payout removed');
            invalidateAll();
            closeSheet();
        },
        onError: (error: Error) => {
            toast.error(`Failed to delete payout: ${error.message}`);
        },
    });

    // ─── Helpers ──────────────────────────────────────────────────────

    const invalidateAll = () => {
        queryClient.invalidateQueries({ queryKey: ['partner-weekly-payouts'] });
        queryClient.invalidateQueries({ queryKey: ['partner-repayments'] });
        queryClient.invalidateQueries({ queryKey: ['partner-payout-details'] });
        queryClient.invalidateQueries({ queryKey: ['all-payout-details'] });
        queryClient.invalidateQueries({ queryKey: ['all-partner-repayments'] });
        queryClient.invalidateQueries({ queryKey: ['unassigned-transactions'] });
        queryClient.invalidateQueries({ queryKey: ['partner-list-summary'] });
    };

    const closeSheet = () => {
        setAssignSheetOpen(false);
        setSelectedRow(null);
        setSelectedTransactionId(null);
        setEditingTransactionId(null);
    };

    const handleAssign = (row: WeeklyPayoutRow) => {
        setSelectedRow(row);
        setSelectedTransactionId(null);
        setEditingTransactionId(null);
        setAssignSheetOpen(true);
    };

    const handleEditPayout = (row: WeeklyPayoutRow) => {
        setSelectedRow(row);
        setSelectedTransactionId(row.payout_transaction_id);
        setEditingTransactionId(row.payout_transaction_id);
        setAssignSheetOpen(true);
    };

    const handleDeletePayout = (transactionId: number | null) => {
        if (!transactionId) return;
        deletePayoutMutation.mutate(transactionId);
    };

    const handleConfirmAssign = () => {
        if (!selectedRow || !selectedTransactionId) return;

        if (editingTransactionId) {
            // Delete old payout rows, then create new
            (async () => {
                try {
                    await supabase
                        .from('partner_payouts')
                        .delete()
                        .eq('transaction_id', editingTransactionId)
                        .not('repayment_id', 'is', null);
                    createPayoutMutation.mutate({
                        transactionId: selectedTransactionId,
                        repaymentIds: selectedRow.repayment_ids,
                    });
                } catch (err: any) {
                    toast.error(`Failed to update payout: ${err.message}`);
                }
            })();
        } else {
            createPayoutMutation.mutate({
                transactionId: selectedTransactionId,
                repaymentIds: selectedRow.repayment_ids,
            });
        }
    };

    // ─── Render ───────────────────────────────────────────────────────

    const selectedWeek = selectedRow ? businessWeekBounds(selectedRow.week_start) : null;

    return (
        <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
            {/* Header */}
            <div className="flex items-start justify-between mb-6">
                <div>
                    <div className="flex items-center gap-3 mb-2">
                        <ClipboardCheck className="h-8 w-8" />
                        <h1 className="text-4xl font-semibold tracking-tight">Payouts</h1>
                    </div>
                    <p className="text-muted-foreground">
                        Assign transactions to weekly partner payouts.
                    </p>
                </div>
                <MonthRangePicker
                    monthRange={monthRange}
                    onMonthRangeChange={handleMonthRangeChange}
                />
            </div>

            {/* Stat Cards */}
            <div className="grid gap-4 md:grid-cols-5">
                <StatCard
                    title="Total Partner Amount"
                    value={<CurrencyDisplay amount={safeStats.totalPartnerAmount} />}
                    description="Total owed to partners this week."
                />
                <StatCard
                    title="Paid"
                    value={<span className="text-3xl font-bold">{safeStats.paidCount}</span>}
                    description="Payouts assigned this week."
                />
                <StatCard
                    title="Due"
                    value={<span className="text-3xl font-bold">{safeStats.dueCount}</span>}
                    description="Payouts awaiting assignment."
                />
                <StatCard
                    title="Total Paid Out"
                    value={<CurrencyDisplay amount={safeStats.totalPaid} />}
                    description="Total amount paid to partners."
                />
                <StatCard
                    title="Partners"
                    value={<span className="text-3xl font-bold">{safeStats.partnerCount}</span>}
                    description="Unique partners with payments."
                />
            </div>

            {/* Filters + Pagination */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="relative w-56">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search partner..."
                            value={partnerSearch}
                            onChange={(e) => setPartnerSearch(e.target.value)}
                            className="pl-9"
                        />
                    </div>
                    <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
                    <SelectTrigger className="w-36">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="due">Due</SelectItem>
                        <SelectItem value="upcoming">Upcoming</SelectItem>
                        <SelectItem value="partial">Partial</SelectItem>
                    </SelectContent>
                    </Select>
                </div>

                    <ListPagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalCount}
                        onPageChange={setCurrentPage}
                    />
            </div>

            {/* Table */}
            {isLoading ? (
                <div className="space-y-4">
                    <Skeleton className="h-64" />
                </div>
            ) : (
                <Card className="border-0">
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Partner</TableHead>
                                    <TableHead>Week</TableHead>
                                    <TableHead>Vehicles</TableHead>
                                    <TableHead>Earnings</TableHead>
                                    <TableHead>Payout</TableHead>
                                    <TableHead>Payout Date</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="w-28">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {(!rows || rows.length === 0) ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center text-muted-foreground py-12">
                                            No payout records found for this week.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((row) => {
                                        const week = businessWeekBounds(row.week_start);
                                        const key = `${row.partner_id}-${row.week_start}`;

                                        return (
                                            <TableRow
                                                key={key}
                                                className="cursor-pointer hover:bg-muted/50"
                                                onClick={() => navigate(`/dashboard/partners/payouts/week?week=${row.week_start}&partner=${row.partner_id}&name=${encodeURIComponent(row.partner_name)}`)}
                                            >
                                                <TableCell className="font-medium">
                                                    {row.partner_name}
                                                </TableCell>
                                                <TableCell className="text-muted-foreground">
                                                    {format(week.weekStart, 'MMM d')} – {format(week.weekEnd, 'MMM d')}
                                                </TableCell>
                                                <TableCell className="text-muted-foreground">
                                                    {row.vehicle_count}
                                                </TableCell>
                                                <TableCell className="text-muted-foreground">
                                                    {formatCurrency(row.total_partner_amount)}
                                                </TableCell>
                                                <TableCell className="font-medium">
                                                    {row.payout_amount > 0
                                                        ? formatCurrency(row.payout_amount)
                                                        : <span className="text-muted-foreground font-normal">—</span>}
                                                </TableCell>
                                                <TableCell className="text-muted-foreground">
                                                    {row.payout_created_at
                                                        ? formatBusinessDate(row.payout_created_at)
                                                        : '—'}
                                                </TableCell>
                                                <TableCell>
                                                    {getStatusBadge(row.status)}
                                                </TableCell>
                                                <TableCell onClick={(e) => e.stopPropagation()}>
                                                    {row.status === 'due' ? (
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => handleAssign(row)}
                                                        >
                                                            Assign
                                                        </Button>
                                                    ) : (row.status === 'paid' || row.status === 'partial') && row.payout_transaction_id ? (
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                                                    <MoreVertical className="h-4 w-4" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                <DropdownMenuItem onClick={() => handleEditPayout(row)}>
                                                                    Edit Payout
                                                                </DropdownMenuItem>
                                                                <AlertDialog>
                                                                    <AlertDialogTrigger asChild>
                                                                        <DropdownMenuItem
                                                                            className="text-destructive"
                                                                            onSelect={(e) => e.preventDefault()}
                                                                        >
                                                                            Delete Payout
                                                                        </DropdownMenuItem>
                                                                    </AlertDialogTrigger>
                                                                    <AlertDialogContent>
                                                                        <AlertDialogHeader>
                                                                            <AlertDialogTitle>Delete Payout</AlertDialogTitle>
                                                                            <AlertDialogDescription>
                                                                                This will unlink the transaction from the repayments. Are you sure?
                                                                            </AlertDialogDescription>
                                                                        </AlertDialogHeader>
                                                                        <AlertDialogFooter>
                                                                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                                            <AlertDialogAction
                                                                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                                                onClick={() => handleDeletePayout(row.payout_transaction_id)}
                                                                            >
                                                                                Delete
                                                                            </AlertDialogAction>
                                                                        </AlertDialogFooter>
                                                                    </AlertDialogContent>
                                                                </AlertDialog>
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    ) : null}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}

            {/* Assignment Sheet */}
            <Sheet open={assignSheetOpen} onOpenChange={(open) => { if (!open) closeSheet(); }}>
                <SheetContent
                    side="right"
                    hideOverlay
                    className="w-[440px] m-4 h-[calc(100vh-32px)] rounded-xl border flex flex-col"
                >
                    <SheetHeader>
                        <SheetTitle>{editingTransactionId ? 'Edit Payout' : 'Assign Payout'}</SheetTitle>
                    </SheetHeader>

                    {selectedRow && selectedWeek && (
                        <div className="mt-6 flex flex-col gap-6 flex-1">
                            {/* Summary */}
                            <div className="space-y-3 rounded-lg border p-4 bg-muted/30">
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">Partner</span>
                                    <span className="font-medium">{selectedRow.partner_name}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">Week</span>
                                    <span className="font-medium">
                                        {format(selectedWeek.weekStart, 'MMM d')} – {format(selectedWeek.weekEnd, 'MMM d, yyyy')}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">Vehicles</span>
                                    <span className="font-medium">{selectedRow.vehicle_count}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-sm text-muted-foreground">Repayments</span>
                                    <span className="font-medium">{selectedRow.repayment_ids.length}</span>
                                </div>
                                <div className="flex justify-between border-t pt-3">
                                    <span className="text-sm font-medium">Partner Amount</span>
                                    <span className="text-lg font-bold">
                                        {formatCurrency(selectedRow.total_partner_amount)}
                                    </span>
                                </div>
                            </div>

                            {/* Transaction Selector */}
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Select Transaction (DEBIT)</label>
                                <TransactionSelector
                                    value={selectedTransactionId}
                                    onChange={(id) => setSelectedTransactionId(id)}
                                    defaultType="DEBIT"
                                    placeholder="Pick a DEBIT transaction..."
                                />
                            </div>

                            {/* Actions */}
                            <div className="mt-auto flex gap-3 pb-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={closeSheet}
                                    className="flex-1"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    disabled={!selectedTransactionId || createPayoutMutation.isPending}
                                    onClick={handleConfirmAssign}
                                    className="flex-1"
                                >
                                    {createPayoutMutation.isPending ? 'Saving...' : (editingTransactionId ? 'Update Payout' : 'Assign Payout')}
                                </Button>
                            </div>

                            {/* Delete option for editing */}
                            {editingTransactionId && (
                                <div className="border-t pt-4">
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <Button variant="destructive" className="w-full">
                                                Delete Payout
                                            </Button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>Delete Payout</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    This will unlink the transaction from the repayments. Are you sure?
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                                <AlertDialogAction
                                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                                    onClick={() => handleDeletePayout(editingTransactionId)}
                                                >
                                                    Delete
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            )}
                        </div>
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
}
